// frontend/src/features/myTask/components/Filters/TeamAccordion.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";
import Avatar from "../../../../components/Avatar";
import { createAvatarName } from "../../../../utils";
import { useDispatch } from "react-redux";
import {
  setGroupUser,
  setAllGroupUser,
} from "../../../../redux/TaskGroupingSlice";
import { useResponsive } from "../../../../hooks/useResponsive";
import { useSearchParams } from "react-router-dom";

const TeamAccordion = React.memo(
  function TeamAccordion({
    team,
    user,
    expandedPanel,
    handleExpandAccordianChange,
    setTeam,
    filteredItems,
    searchQuery,
  }) {
    const dispatch = useDispatch();
    const isMobile = useResponsive("down", "md");
    const [searchParams, setSearchParams] = useSearchParams();

    const isExpandedFromPanel = useMemo(() => {
      return expandedPanel.find(
        (panel) => panel.team_id === team.team_id
      )?.isExpand || false;
    }, [expandedPanel, team.team_id]);

    const [localExpanded, setLocalExpanded] = useState(false);
    const [manualOverride, setManualOverride] = useState(false);

    const shouldBeExpanded = searchQuery && !manualOverride ? isExpandedFromPanel : localExpanded;

    useEffect(() => {
      if (searchQuery && !manualOverride) {
        setLocalExpanded(isExpandedFromPanel);
      } else if (!searchQuery) {
        setLocalExpanded(false);
        setManualOverride(false);
      }
    }, [searchQuery, isExpandedFromPanel, manualOverride]);

    const teamUsers = useMemo(() => {
      if (!team.users.length || !filteredItems.length) return [];
      
      const selectedUserIds = new Set(user.map(u => u.user_id));
      
      return team.users
        .filter((usr) =>
          filteredItems.some((user) => user.user_id === usr.user_id)
        )
        .sort((a, b) => {
          const aSelected = selectedUserIds.has(a.user_id);
          const bSelected = selectedUserIds.has(b.user_id);
          if (aSelected !== bSelected) {
            return bSelected - aSelected;
          }
          return a.first_name.localeCompare(b.first_name);
        });
    }, [team.users, filteredItems, user]);

    const updateQueryParams = useCallback((userIds, shouldAdd) => {
      const newSearchParams = new URLSearchParams(searchParams);
      const currentUidParam = newSearchParams.get("uid") || "";
      const currentUserIds = currentUidParam ? currentUidParam.split(",") : [];

      if (shouldAdd) {
        const newUserIds = [...new Set([...currentUserIds, ...userIds])];
        newSearchParams.set("uid", newUserIds.join(","));
      } else {
        const newUserIds = currentUserIds.filter(id => !userIds.includes(id));
        if (newUserIds.length > 0) {
          newSearchParams.set("uid", newUserIds.join(","));
        } else {
          newSearchParams.delete("uid");
        }
      }

      setSearchParams(newSearchParams);
    }, [searchParams, setSearchParams]);

    const areAllTeamMembersSelected = useMemo(() => {
      if (!isMobile || teamUsers.length === 0) return false;

      const currentUidParam = searchParams.get("uid") || "";
      const currentUserIds = currentUidParam ? currentUidParam.split(",") : [];

      return teamUsers.every((user) =>
        currentUserIds.includes(user.user_id.toString())
      );
    }, [searchParams, teamUsers, isMobile]);

    const handleChange = useCallback((event, isExpanded) => {
      setLocalExpanded(isExpanded);
      if (searchQuery) {
        setManualOverride(true);
      }
      handleExpandAccordianChange(team.team_id)(event, isExpanded);
    }, [searchQuery, handleExpandAccordianChange, team.team_id]);

    const handleTeamCheckboxClick = useCallback((event) => {
      event.stopPropagation();

      if (isMobile) {
        setTeam(
          team,
          team?.users?.every((ele) =>
            user.some((element) => element.user_id == ele.user_id)
          )
        );
        return;
      }

      const shouldAdd = !areAllTeamMembersSelected;
      const teamUserIds = teamUsers.map((user) => user.user_id.toString());
      
      updateQueryParams(teamUserIds, shouldAdd);

      if (shouldAdd) {
        // Add all team users to Redux state
        teamUsers.forEach((user) => {
          dispatch(setGroupUser(user));
        });
      } else {
        // Remove all team users from Redux state
        dispatch(setAllGroupUser([]));
        
        const currentUidParam = searchParams.get("uid") || "";
        const currentUserIds = currentUidParam ? currentUidParam.split(",") : [];
        
        const otherTeamsUsers = user.filter(
          (u) =>
            !teamUserIds.includes(u.user_id.toString()) &&
            currentUserIds.includes(u.user_id.toString())
        );

        otherTeamsUsers.forEach((user) => {
          dispatch(setGroupUser(user));
        });
      }
    }, [isMobile, setTeam, team, user, areAllTeamMembersSelected, teamUsers, updateQueryParams, dispatch, searchParams]);

    const handleUserClick = useCallback((el) => {
      if (isMobile) {
        dispatch(setGroupUser(el));
        return;
      }

      const isUserSelected = user.some(
        (element) => element.user_id == el.user_id
      );
      
      updateQueryParams([el.user_id.toString()], !isUserSelected);

      // Update Redux state
      if (!isUserSelected) {
        dispatch(setGroupUser(el));
      } else {
        dispatch(setAllGroupUser([]));
        
        const currentUidParam = searchParams.get("uid") || "";
        const currentUserIds = currentUidParam ? currentUidParam.split(",") : [];
        
        const otherUsers = user.filter(
          (u) =>
            u.user_id !== el.user_id &&
            currentUserIds.includes(u.user_id.toString())
        );

        otherUsers.forEach((user) => {
          dispatch(setGroupUser(user));
        });
      }
    }, [isMobile, dispatch, user, updateQueryParams, searchParams]);

    return (
      <Accordion
        expanded={shouldBeExpanded}
        onChange={handleChange}
        className="team-accordion">
        <Tooltip
          title={searchQuery && shouldBeExpanded ? "Click to collapse" : ""}
          placement="top"
          arrow
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            disableGutters
            className="accordion-summary">
            <Box onClick={(event) => event.stopPropagation()}>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={team?.users?.every((ele) =>
                    user.some((element) => element.user_id === ele.user_id)
                  )}
                  onClick={handleTeamCheckboxClick}
                  onChange={() => { }}
                />
                <span className="checkmark"></span>
              </label>
            </Box>
            <Box className="team-name-box">
              <Typography>{team.team}</Typography>
            </Box>
          </AccordionSummary>
        </Tooltip>
        <AccordionDetails className="accordion-details">
          {teamUsers.map((el, index) => {
            return (
              filteredItems.some((user) => user.user_id === el.user_id) && (
                <CustomMenuItem
                  key={`user-avatar-dropdown-admin-assign-${el.user_id}`}
                  onClick={() => handleUserClick(el)}>
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      id={`user-check-box-${el.user_id}`}
                      checked={user.some(
                        (element) => element.user_id == el.user_id
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={() => {}}
                    />
                    <span className="checkmark"></span>
                    <Avatar
                      key={`user-avatar-${el.user_id}`}
                      username={`${el.first_name} ${el.last_name}`}
                      user_image={el.user_image}
                      title={`${el.first_name} ${el.last_name}`}
                      className="user-avatar-dropdown user-avatar mr-8">
                      {createAvatarName(`${el.first_name} ${el.last_name}`)}
                    </Avatar>
                    {`${el.first_name} ${el.last_name}`}
                  </label>
                </CustomMenuItem>
              )
            );
          })}
        </AccordionDetails>
      </Accordion>
    );
  },
  (prevProps, nextProps) => {
    // For React.memo, return true to prevent re-render, false to allow re-render
    // We want to re-render when any of the props change

    // Check if any of the props have changed
    const teamChanged =
      JSON.stringify(prevProps.team) !== JSON.stringify(nextProps.team);
    const userChanged =
      JSON.stringify(prevProps.user) !== JSON.stringify(nextProps.user);
    const expandedPanelChanged =
      JSON.stringify(prevProps.expandedPanel) !==
      JSON.stringify(nextProps.expandedPanel);
    const searchQueryChanged = prevProps.searchQuery !== nextProps.searchQuery;
    const filteredItemsChanged =
      JSON.stringify(prevProps.filteredItems) !==
      JSON.stringify(nextProps.filteredItems);

    // Return false to trigger re-render when any prop has changed
    return (
      !teamChanged &&
      !userChanged &&
      !expandedPanelChanged &&
      !searchQueryChanged &&
      !filteredItemsChanged
    );
  }
);

export default TeamAccordion;
